import {h, Component} from 'preact/dist/preact';
import cx from 'classnames';
import {box, labels, nav} from './styles';
import {withRouter, NavLink} from 'react-router-dom';
import ProgressMeter from '@components/common/ProgressMeter';
import {UPS_TRACKING_URL} from '@util/URLUtils';
import {formatPhoneUS} from '@util/StringUtils';
import {$CONTENT_WINDOW} from '@config/selectors';
import Icon from '@components/common/Icon';
import {getReturnByDate, getUnitStatuses, hasUnreceivedItems, VENDOR_LABELS} from './helpers';
import {Address} from '@components/common/Address';
import {ShippingLabel} from '@containers/SmsPortal/ShippingLabel';
import {format} from 'date-fns';
import Button from '@components/common/Button';
import _ScrollToTop from '@components/common/ScrollToTop';

export {
    EquipmentListHeader, EquipmentResultItem, EquipmentItem, EquipmentModelDetail, EquipmentListEmpty
} from './Equipment';
export {RmaList, RmaLineItem, RmaLineItemShort, RmaConfirmationItem, RmaItem, RmaListError} from './Rma';
export {CheckoutLineItem, CheckoutButton} from './Checkout';
export {ShippingLabel, ShippingLabelError} from './ShippingLabel';

export const ScrollToTop = withRouter( _ScrollToTop );

export const PageHeading = props =>
    <div className="my-4 md:mt-10 md:mb-6 contain-content pad-content">
        <h1 className="text-2xl md:text-3xl font-thin">{props.title}</h1>
        {props.subTitle && <p className="text-base max-w-lg leading-tight">{props.subTitle}</p>}
    </div>;

export const PageNavigation = props => {
    return (
        <nav className={nav.container}>
            <NavLink
                data-testid="sms-tab-my-equipment"
                className={nav.link}
                isActive={() => /^\/my-equipment/.test( props.pathname )}
                activeClassName="border-brand"
                to="/my-equipment">
                My Equipment
            </NavLink>
            <NavLink
                data-testid="sms-tab-my-repairs"
                className={nav.link}
                isActive={() => /^\/open-repairs/.test( props.pathname )}
                activeClassName="border-brand"
                to="/open-repairs">
                Open Repairs
            </NavLink>
            <NavLink
                data-testid="sms-tab-my-repair-history"
                className={nav.link}
                isActive={() => /^\/repair-history/.test( props.pathname )}
                activeClassName="border-brand"
                to="/repair-history">
                Repair History
            </NavLink>
            <NavLink
                data-testid="sms-tab-cart"
                className={nav.link}
                isActive={() => /^\/cart/.test( props.pathname )}
                activeClassName="border-brand"
                to="/cart">
                Cart
            </NavLink>
        </nav>
    );
};

export const CallRepairsLink = () => <a className="txt-c1 underline md:no-underline"
                                        data-testid="sms-cs-contact"
                                        href={`tel:+${upi.conf.phoneEquipmentRepairs}`}>{upi.conf.phoneEquipmentRepairs}</a>;

export const BackButton = ( {history, className = ''} ) =>
    <Button data-testid="sms-back-button" ghosted onClick={history.goBack} className={className}>
        <Icon.ui name="arrow-return" center className="mr-2 text-lg"/>
        Back
    </Button>;

export const NumberedHeading = props => (
    <div className={`flex items-center ${props.className || ''}`}>
        <span className={labels.step}>{props.step || 1}</span>
        <h2 className="ml-2 text-lg md:text-xl font-normal">{props.children}</h2>
    </div>
);

export const VendorLabel = ( {name, className = ''} ) =>
    <span className={`whitespace-no-wrap ${className}`}>{VENDOR_LABELS[name]}</span>;

export const PageContent = ( {className = '', children} ) =>
    <div className={`contain-content float-left w-full ${className}`}>{children}</div>;

export const ContactInfo = ( {firstName, lastName, phone, email, className = ''} ) =>
    <div className={className}>
        <span className="block">{firstName} {lastName}</span>
        {phone && <span className="block">Phone: {formatPhoneUS( phone )}</span>}
        {email && <span className="block">{email}</span>}
    </div>;

export const SmsPageError = () =>
    <PageContent>
        <div className="mt-4">
            <div className="text-center mt-8 max-w-sm mx-auto">
                <h2 className="text-lg font-normal px-8">Sorry, there was a problem while trying to retrieve the
                    information for this page</h2>
                <p className="mt-4">This could be due to a problem on our end or a poor internet connection. Please try
                    refreshing.</p>
                <button className="button secondary mt-8" onClick={() => window.location.reload()}>Refresh page</button>
                <p className={`mt-16 ${box.info}`}>
                    If the problem continues please contact us at
                    <span className="whitespace-no-wrap">{upi.conf.phoneTollFree}</span>.
                </p>
            </div>
        </div>
    </PageContent>;

export const UnitStatus = props => {
    const {description, sequence, steps} = getUnitStatuses( props.unitStatus );
    const totalSteps = 5;
    // currently 5 possible steps, but sequences range from 1..6
    const value = sequence / totalSteps;
    // subtract 0.1 for offset to display step as half complete
    const progressValue = value > 1
        ? 1
        : value;
    // : value - ((1 / totalSteps) / 3);

    return (
        <span data-testid="unit-status-meter" className="my-2 flex w-full items-center justify-end">
            <span className="pt-2 relative z-0 text-sm font-bold text-center w-full">
                <ul className="absolute z-10 h-1 w-full flex justify-between list-reset m-0 mb-1 font-normal text-xs txt-c2 leading-collapse">
                    {steps.map( ( status, i, arr ) => {
                        const isCurrent = Math.floor( sequence ) === i;
                        const isDone = Math.floor( sequence ) > i;
                        const stepClass = cx(
                            '-ml-px z-1 self-center leading-none font-bold flex items-center justify-center rounded-full border-solid',
                            isDone
                                ? ['bg-success', 'txt-white', 'border-none']
                                : ['bg-white', 'txt-c2', 'border-2'],
                            isCurrent
                                ? ['border-success', 'w-6', 'h-6']
                                : ['border-grey-lightest', 'w-5', 'h-5']
                        );

                        return (
                            <li className="flex flex-1">
                                <span data-tooltip={`(${i + 1} of ${arr.length}) ${status.description}`}
                                      className={stepClass}>
                                    {isDone
                                        ? <Icon.ui name="checkmark"
                                                   wrapperClassName="pointer-events-none"
                                                   className="text-white h-2"/>
                                        : i + 1
                                    }
                                </span>
                                <span className="skew-x--20 flex-1 border-r-4 border-solid border-white">&nbsp;</span>
                            </li>
                        );
                    } )}
                </ul>

                <ProgressMeter height="1" className="skew-x--20 m-0 p-0" color="success" value={progressValue}/>
                <span className="inline-block mt-3">{description}</span>
            </span>
    </span>
    );
};

export const TrackingLink = ( {trackingNumber, className = ''} ) =>
    <a className={`block link-2 tracking-wide uppercase ${className}`}
       target="_blank" href={UPS_TRACKING_URL + trackingNumber}>
        {trackingNumber}&nbsp;&raquo;
    </a>;

export const InjuryBadge = ( {hasInjury} ) =>
    hasInjury ? <span className={`mt-1 text-sm text-fail italic`}>Injury reported</span> : null;

export const RmaStatusBadge = ( {status, className = ''} ) => {
    const classList = cx(
        labels.badge,
        className,
        {
            'text-fail': status === 'CANCELED',
            'text-success': /COMPLETE|CLOSED/.test( status )
        }
    );
    return (
        <span className={classList}>{status === 'CLOSED' ? 'COMPLETE' : status}</span>
    );
};

export const RmaDateBadge = ( {date, className = ''} ) =>
    <p className={`flex ${className}`}>
        <Icon.ui name="calendar" className="text-lg mr-2" center/>
        <span>Created {format( date, 'MMMM D, YYYY' )}</span>
    </p>;

export class RmaStatusMeter extends Component {

    itemClass = 'flex items-center';
    itemActive = 'txt-c1 my-4 ';
    itemInactive = 'txt-c3';
    itemComplete = 'txt-c1';
    headingClass = '';
    headingActive = 'text-lg';
    headingInactive = 'text-base font-normal';
    iconClass = 'flex items-center justify-center w-12 h-12 block bg-white rounded-full';
    iconActive = 'radial-signal border-2 border-brand border-solid';

    listStyle = {
        backgroundImage: 'linear-gradient( to bottom, rgba(200,201,199,1), rgba(200,201,199,1))',
        backgroundSize: '2px',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'calc(1.5rem - 1px)'
    };

    constructor( props ) {
        super( props );
        this.isWaiting = props.status === 'waiting';
        this.isReceived = props.status === 'received';
        this.isComplete = props.status === 'complete';
        this.step1Complete = !this.isWaiting;
        this.step2Complete = this.isComplete;
    }

    renderTrackingInfo() {
        const {inboundTrackingNumber, createdDate, vendor, vendorAddress} = this.props;

        return (
            <div>
                <p className={`mt-1 max-w-sm ${box.info}`}>
                    All items must be shipped by <strong>{
                    format( getReturnByDate( createdDate ), 'MMMM D, YYYY' )
                }</strong> to be eligible for repair.
                </p>
                <h4 className="font-normal mt-2">Ship to {VENDOR_LABELS[vendor]}</h4>
                <Address {...vendorAddress}/>
                <p className="text-ms mt-2">
                    Tracking number <TrackingLink className="inline" trackingNumber={inboundTrackingNumber}/>
                </p>
                <div className="mt-4">
                    <a href={UPS_TRACKING_URL + inboundTrackingNumber} target="_blank"
                       className="button secondary small">
                        Track <Icon.ui name='link-external' center className='ml-2 text-lg'/>
                    </a>
                    <ShippingLabel className="inline ml-2" trackingNumber={inboundTrackingNumber}/>
                </div>
            </div>
        );
    }

    render() {
        if ( this.props.status === 'canceled' || this.props.status === 'closed' ) {
            return null;
        }

        const {itemActive, itemClass, itemComplete, itemInactive, headingActive, headingClass, headingInactive, iconActive, iconClass, listStyle} = this;

        return (
            <ul data-testid="shipping-status-list" style={listStyle}
                className="sms-shipping-status flex-1 list-reset my-8 md:my-0">
                <li className={cx( itemClass, [this.isWaiting ? itemActive : itemComplete] )}>
                    <div className="self-start">
                        <span className={cx( iconClass, {[iconActive]: this.isWaiting} )}>
                        <Icon.ui name={this.step1Complete ? 'checkmark' : 'box-shipment'} center
                                 className="text-lg"/>
                        </span>
                    </div>
                    <div className="flex-1 ml-8">
                        <h3 className={cx( headingClass, [this.isWaiting ? headingActive : headingInactive] )}>
                            {this.isWaiting ? 'Waiting to receive your equipment' : 'Equipment received'}
                        </h3>
                        {this.isWaiting && this.renderTrackingInfo()}
                    </div>
                </li>
                <li className={cx( itemClass,
                    this.isReceived ? itemActive : this.isComplete ? itemComplete : itemInactive )}>
                    <div>
                        <span className={cx( iconClass, {[iconActive]: this.isReceived} )}>
                        <Icon.ui name={this.step2Complete ? 'checkmark' : 'tools'} center className="text-lg"/>
                        </span>
                    </div>
                    <div className="flex-1 ml-8">
                        <h3 className={cx( headingClass, [this.isReceived ? headingActive : headingInactive] )}>
                            {this.isComplete ? 'Repairs complete' : this.isReceived ? 'In progress' : 'Perform repairs'}
                        </h3>
                        {this.isReceived &&
                        <p>We have received your equipment and the repair process has begun.</p>
                        }
                    </div>
                </li>
                <li className={cx( itemClass, this.isComplete ? itemActive : itemInactive )}>
                    <div className="self-end">
                        <span className={cx( iconClass, {[iconActive]: this.isComplete} )}>
                        <Icon.ui name="flag-finish" center className="text-lg"/>
                        </span>
                    </div>
                    <div className="flex-1 ml-8">
                        <h3 className={cx( headingClass, [this.isComplete ? headingActive : headingInactive] )}>
                            {this.isComplete ? 'Finished!' : 'Finalize process'}
                        </h3>
                        {this.isComplete &&
                        <p>The repair process is complete and your repaired equipment is being returned.</p>
                        }
                    </div>
                </li>
            </ul>
        );
    }
}
